import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { StripePriceDto } from '@/features/billing/types';
import { InputLabel } from '../ui/InputLabel';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { fetchPromoCode, fetchPrices } from '@/features/billing/billingThunks';
import { updateWorkspace } from '@/features/workspace/workspaceThunks';
import { useTranslation } from 'react-i18next';

type BillingIntervalSelectorProps = {
  prices: StripePriceDto[] | null;
  selectedPlanKey: string | null;
  isYearly: boolean;
  onIsYearlyChange: (value: boolean) => void;
};

export default function BillingIntervalSelector({
  prices: _prices,
  selectedPlanKey: _selectedPlanKey,
  isYearly: _isYearly,
  onIsYearlyChange: _onIsYearlyChange,
}: BillingIntervalSelectorProps) {
  const { t } = useTranslation('billing');
  const dispatch = useAppDispatch();
  const currentWorkspace = useAppSelector((s) => s.workspace.currentWorkspace);
  const promoCode = currentWorkspace?.promoCode;
  const promoCodeLoading = useAppSelector((s) => s.billing.promoCode.loading);

  const [inputValue, setInputValue] = useState('');
  const [promoError, setPromoError] = useState<string | null>(null);

  const workspaceId = currentWorkspace?.id;
  const handleApplyPromo = async () => {
    setPromoError(null);
    const code = inputValue.trim();
    const workspace = currentWorkspace;
    if (!code || !workspace || workspaceId == null) return;
    const result = await dispatch(fetchPromoCode({ workspaceId, code }));
    if (fetchPromoCode.fulfilled.match(result) && result.payload.active) {
      await dispatch(
        updateWorkspace({
          workspaceId,
          input: {
            name: workspace.name,
            timezone: workspace.timezone ?? 'Europe/Paris',
            promoCode: result.payload.code,
          },
        }),
      );
      await dispatch(fetchPrices({ workspaceId, promoCode: result.payload.code }));
      setInputValue('');
    } else {
      if (fetchPromoCode.rejected.match(result) && result.payload) {
        const isNotFound =
          result.payload.code === 'NOT_FOUND' || result.payload.message === 'errors.notFound';
        setPromoError(isNotFound ? t('promoCode.errorNotFound') : result.payload.message);
      } else if (fetchPromoCode.fulfilled.match(result) && !result.payload.active) {
        setPromoError(t('promoCode.errorInvalid'));
      } else {
        setPromoError(t('promoCode.errorNotFound'));
      }
    }
  };

  return (
    <div className="mt-6 rounded-lg">
      {promoCode ? (
        <div className=" p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-sm font-medium text-gray-600 mb-1">{t('promoCode.applied')}</p>
          <p className="text-base font-mono font-medium text-gray-800 select-none">{promoCode}</p>
        </div>
      ) : (
        <div className="mb-4 space-y-4">
          <Label className="text-lg font-medium">{t('promoCode.addLabel')}</Label>{' '}
          <div>
            <InputLabel
              label={t('promoCode.inputLabel')}
              type="text"
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setPromoError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleApplyPromo();
                }
              }}
            />
            {promoError && <p className="text-xs text-error mt-2">{promoError}</p>}
          </div>
          <Button
            type="button"
            className="w-full"
            onClick={handleApplyPromo}
            disabled={promoCodeLoading || !inputValue.trim() || workspaceId == null}
          >
            {promoCodeLoading ? t('promoCode.verifying') : t('promoCode.apply')}
          </Button>
        </div>
      )}
    </div>
  );
}
